import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react'
import {
  ArrowsRightLeftIcon,
  ChartBarSquareIcon,
  ChatBubbleLeftRightIcon,
  DocumentMagnifyingGlassIcon,
  LightBulbIcon,
  MegaphoneIcon,
  PresentationChartLineIcon,
  RocketLaunchIcon,
  SignalIcon,
  SparklesIcon,
  Squares2X2Icon,
  UserGroupIcon,
  UserIcon,
  UsersIcon,
} from '@heroicons/react/24/outline'
import { domAnimation, LazyMotion, m, useReducedMotion } from 'motion/react'
import {
  ArrowRight,
  ChartNoAxesCombined,
  Combine,
  DoorOpen,
  Gauge,
  LockKeyhole,
  Network,
  PersonStanding,
  Radar,
  RadioTower,
  Rocket,
  ScanSearch,
  Telescope,
  Workflow,
  type LucideIcon,
} from 'lucide-react'
import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import {
  Hero,
  FinalSection,
  PlatformSection,
  ResultsFirstSection,
  ReviewDialog,
  SiteHeader,
  type ReviewSource,
} from './App'
import { OperatingProofWall } from './components/PremiumMechanics'
import { personas } from './content'

const trustedTeams = [
  ['KITEWORK', 'Moved off manual prospecting'],
  ['FoldPay', 'Migrated from Outreach + sheets'],
  ['VYNE', 'Unified data and sequences'],
  ['MONO', 'Scaled sourced outbound'],
  ['Marble', 'Automated signal routing'],
  ['Orchid', 'Lifted meeting quality'],
  ['BRISK', 'Replaced 5 GTM tools'],
  ['Rill', 'Improved inbox placement'],
] as const

type TrustedTeam = (typeof trustedTeams)[number]

export type CombinedVariant =
  | 'control'
  | 'centered'
  | 'proof-motion'
  | 'proof-flip'
  | 'proof-flip-specialists'
  | 'proof-flip-specialists-polished'

export function resolveCombinedVariant(pathname: string): CombinedVariant {
  const route = (pathname.split(/[?#]/, 1)[0] || '/').replace(/\/+$/, '') || '/'
  if (route === '/centered') return 'centered'
  if (route === '/proof-motion') return 'proof-motion'
  if (route === '/proof-flip') return 'proof-flip'
  if (route === '/proof-flip-specialists') return 'proof-flip-specialists'
  if (route === '/proof-flip-specialists-polished') return 'proof-flip-specialists-polished'
  return 'control'
}

const personaIcons: readonly LucideIcon[] = [Telescope, Gauge, Combine, Network, Radar]

const personaRoleGlyphs = [
  { Icon: ScanSearch, role: 'technical' },
  { Icon: ChartNoAxesCombined, role: 'leadership' },
  { Icon: Rocket, role: 'founder' },
  { Icon: Workflow, role: 'operations' },
  { Icon: RadioTower, role: 'marketing' },
] as const satisfies readonly { Icon: LucideIcon; role: string }[]

const specialistPersonaScenes = [
  {
    PersonIcon: UserIcon,
    SignalIcon: ChatBubbleLeftRightIcon,
    ToolIcon: DocumentMagnifyingGlassIcon,
    role: 'technical',
  },
  {
    PersonIcon: UserGroupIcon,
    SignalIcon: ChartBarSquareIcon,
    ToolIcon: PresentationChartLineIcon,
    role: 'leadership',
  },
  {
    PersonIcon: UserIcon,
    SignalIcon: LightBulbIcon,
    ToolIcon: RocketLaunchIcon,
    role: 'founder',
  },
  {
    PersonIcon: UsersIcon,
    SignalIcon: ArrowsRightLeftIcon,
    ToolIcon: Squares2X2Icon,
    role: 'operations',
  },
  {
    PersonIcon: UserIcon,
    SignalIcon,
    ToolIcon: MegaphoneIcon,
    role: 'marketing',
  },
] as const

const polishedSpecialistPersonaScenes = [
  {
    PersonIcon: PersonStanding,
    SignalIcon: ChatBubbleLeftRightIcon,
    ToolIcon: DocumentMagnifyingGlassIcon,
    fieldShape: 'low-pool',
    pose: 'analyst',
    role: 'technical',
  },
  {
    PersonIcon: PersonStanding,
    SignalIcon: ChartBarSquareIcon,
    ToolIcon: PresentationChartLineIcon,
    fieldShape: 'side-spotlight',
    pose: 'conductor',
    role: 'leadership',
  },
  {
    PersonIcon: PersonStanding,
    SignalIcon: LightBulbIcon,
    ToolIcon: RocketLaunchIcon,
    fieldShape: 'vertical-ribbon',
    pose: 'builder',
    role: 'founder',
  },
  {
    PersonIcon: PersonStanding,
    SignalIcon: ArrowsRightLeftIcon,
    ToolIcon: Squares2X2Icon,
    fieldShape: 'operating-grid',
    pose: 'operator',
    role: 'operations',
  },
  {
    PersonIcon: PersonStanding,
    SignalIcon,
    ToolIcon: MegaphoneIcon,
    fieldShape: 'signal-cloud',
    pose: 'broadcaster',
    role: 'marketing',
  },
] as const

const precisionEase: [number, number, number, number] = [0.23, 1, 0.32, 1]

const currentResultCards = [
  {
    kind: 'metric',
    label: 'open rate',
    record: 'Open-rate source record',
    tone: 'yellow',
    value: '78%+',
  },
  {
    kind: 'metric',
    label: 'enterprise opportunities',
    record: 'Opportunity source record',
    tone: 'mint',
    value: '150+',
  },
  {
    attribution: 'Priya Shah — VP Growth',
    kind: 'quote',
    quote:
      'Wave99 helped revive dormant industrial accounts and create over $2M in qualified pipeline in 10 months.',
    tone: 'paper',
  },
  {
    attribution: 'Nolan Burke — Specialty Manufacturing',
    kind: 'quote',
    quote:
      'Automated multichannel sequences replaced manual follow-up and kept every message tied to approved proof.',
    tone: 'paper',
  },
  {
    kind: 'metric',
    label: 'saved per rep weekly',
    record: 'Time-saved source record',
    tone: 'yellow',
    value: '2h',
  },
  {
    kind: 'metric',
    label: 'interested reply rate',
    record: 'Reply-rate source record',
    tone: 'pink',
    value: '25%',
  },
  {
    kind: 'metric',
    label: 'ROI on signals',
    record: 'Signal ROI source record',
    tone: 'violet',
    value: '9x',
  },
  {
    kind: 'metric',
    label: 'customer base growth',
    record: 'Growth source record',
    tone: 'pink',
    value: '4x',
  },
  {
    attribution: 'Elian Frost — Revenue Operations',
    kind: 'quote',
    quote:
      'The team now works from one outbound room: signals, copy, inbox, analytics, and deliverability.',
    tone: 'paper',
  },
] as const

const customerLoveItems = [
  {
    label: 'Agnieszka Roy — BrightLedger',
    title: 'The UI makes sequence management and account research feel like one workflow.',
  },
  {
    label: 'Lewis Bell — Deelpark',
    title:
      'We get new trigger points for people who changed jobs, visited pages, or showed competitor pain.',
  },
  {
    label: 'Lucas Summers — HPE Labs',
    title: 'Inbox placement improved because deliverability is not bolted on after launch.',
  },
  {
    label: 'Aidan Aguirre — Centage',
    title:
      'Conditional messaging, A/B testing, and intent triggers helped us scale without generic outreach.',
  },
  {
    label: 'Mei Tan — Orbit Works',
    title: 'The copy feels personal because it is tied to evidence, not vague AI fluff.',
  },
  {
    label: 'Luke Shelby — Tidal',
    title: 'During the trial, we generated six meetings from signal-led sequences.',
  },
] as const

function canFollowTrustPointer(event: ReactPointerEvent<HTMLUListElement>) {
  if (event.pointerType !== 'mouse') return false
  if (typeof window.matchMedia !== 'function') return true

  return (
    window.matchMedia('(hover: hover) and (pointer: fine)').matches &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

function releaseTrustTile(tile: HTMLLIElement | null) {
  if (!tile) return
  tile.style.setProperty('--trust-follow-x', '0px')
  tile.style.setProperty('--trust-follow-y', '0px')
  tile.style.setProperty('--trust-tilt-x', '0deg')
  tile.style.setProperty('--trust-tilt-y', '0deg')
  tile.style.setProperty('--trust-content-x', '0px')
  tile.style.setProperty('--trust-content-y', '0px')
  delete tile.dataset.pointerActive
}

function TrustTeamGrid() {
  const gridRef = useRef<HTMLUListElement>(null)
  const activeTileRef = useRef<HTMLLIElement | null>(null)
  const pointerFrameRef = useRef<number | null>(null)
  const pendingPointerRef = useRef<{
    clientX: number
    clientY: number
    tile: HTMLLIElement
  } | null>(null)

  useEffect(() => () => {
    if (pointerFrameRef.current !== null) {
      window.cancelAnimationFrame(pointerFrameRef.current)
    }
  }, [])

  const leaveGrid = () => {
    if (pointerFrameRef.current !== null) {
      window.cancelAnimationFrame(pointerFrameRef.current)
      pointerFrameRef.current = null
    }
    pendingPointerRef.current = null
    releaseTrustTile(activeTileRef.current)
    activeTileRef.current = null
  }

  const followPointer = (event: ReactPointerEvent<HTMLUListElement>) => {
    const grid = gridRef.current
    if (!grid || !canFollowTrustPointer(event) || !(event.target instanceof Element)) return

    const tile = event.target.closest('li') as HTMLLIElement | null
    if (!tile || !grid.contains(tile)) return

    if (activeTileRef.current !== tile) {
      releaseTrustTile(activeTileRef.current)
      activeTileRef.current = tile
      tile.dataset.pointerActive = 'true'
    }

    pendingPointerRef.current = { clientX: event.clientX, clientY: event.clientY, tile }
    if (pointerFrameRef.current !== null) return

    pointerFrameRef.current = window.requestAnimationFrame(() => {
      pointerFrameRef.current = null
      const latestPointer = pendingPointerRef.current
      if (!latestPointer || activeTileRef.current !== latestPointer.tile) return

      const currentTile = latestPointer.tile
      const rect = currentTile.getBoundingClientRect()
      if (rect.width <= 0 || rect.height <= 0) return

      const localX = Math.max(0, Math.min(rect.width, latestPointer.clientX - rect.left))
      const localY = Math.max(0, Math.min(rect.height, latestPointer.clientY - rect.top))
      const x = localX / rect.width - 0.5
      const y = localY / rect.height - 0.5

      currentTile.style.setProperty('--trust-follow-x', `${(x * 14).toFixed(2)}px`)
      currentTile.style.setProperty('--trust-follow-y', `${(y * 10).toFixed(2)}px`)
      currentTile.style.setProperty('--trust-tilt-x', `${(-y * 2).toFixed(2)}deg`)
      currentTile.style.setProperty('--trust-tilt-y', `${(x * 2.4).toFixed(2)}deg`)
      currentTile.style.setProperty('--trust-content-x', `${(x * 5).toFixed(2)}px`)
      currentTile.style.setProperty('--trust-content-y', `${(y * 4).toFixed(2)}px`)
      currentTile.style.setProperty('--trust-glow-x', `${((localX / rect.width) * 100).toFixed(1)}%`)
      currentTile.style.setProperty('--trust-glow-y', `${((localY / rect.height) * 100).toFixed(1)}%`)
    })
  }

  return (
    <ul
      aria-label="Customer teams"
      className="combined-logo-grid"
      data-hover-mechanic="shared-grid-pointer-follow"
      onPointerCancel={leaveGrid}
      onPointerLeave={leaveGrid}
      onPointerMove={followPointer}
      ref={gridRef}
    >
      {trustedTeams.map(([name, outcome]) => (
        <li key={name}>
          <div className="combined-logo-card">
            <strong>{name}</strong>
            <span>{outcome}</span>
          </div>
        </li>
      ))}
    </ul>
  )
}

function CurrentTrustSection() {
  return (
    <section
      aria-labelledby="combined-trust-title"
      className="combined-trust-section"
      data-proof-status="requires-publication-approval"
      data-sector="current-trust"
      id="trust"
    >
      <h2 id="combined-trust-title">Trusted by modern outbound teams</h2>
      <TrustTeamGrid />
      <figure className="combined-pull-quote">
        <blockquote>
          “Wave99 is like a top SDR, data analyst, and industry researcher in one workspace. Our reps know who to contact and exactly why now.”
        </blockquote>
        <figcaption>
          <span aria-hidden="true" className="combined-portrait" />
          <span className="combined-quote-author">
            <b>Mara Lin</b>
            <small>VP Growth, Kitework Industrial</small>
          </span>
          <strong>kitework</strong>
        </figcaption>
      </figure>
    </section>
  )
}

function OrchidTestimonialSection() {
  return (
    <section
      aria-label="Orchid testimonial"
      className="combined-dark-testimonial"
      data-proof-status="requires-publication-approval"
      data-sector="current-orchid-testimonial"
      id="orchid-testimonial"
    >
      <figure className="combined-dark-quote">
        <blockquote>
          “Every morning we get the accounts, reasons, and messages that matter. It turned outbound from guesswork into a repeatable system.”
        </blockquote>
        <figcaption>
          <span aria-hidden="true" className="combined-portrait combined-portrait-orchid" />
          <span className="combined-quote-author">
            <b>Jonas Vale</b>
            <small>Head of Sales, Orchid</small>
          </span>
          <strong>orchid</strong>
        </figcaption>
      </figure>
    </section>
  )
}

function PersonaRoleGlyph({
  glyph,
  reducedMotion,
  selected,
}: {
  glyph: (typeof personaRoleGlyphs)[number]
  reducedMotion: boolean
  selected: boolean
}) {
  const { Icon, role } = glyph
  const animateSelection = selected && !reducedMotion
  const transition = {
    duration: reducedMotion ? 0 : 0.64,
    ease: precisionEase,
  }

  return (
    <div
      aria-hidden="true"
      className={`combined-persona-tab-visual combined-persona-role-glyph is-${role}`}
      data-glyph-state={selected ? 'selected' : 'idle'}
      data-role-glyph={role}
    >
      <span className="combined-persona-glyph-grid" />
      <m.span
        animate={animateSelection
          ? { opacity: [0.18, 0.72, 0.5], scale: [0.82, 1.12, 1] }
          : { opacity: selected ? 0.5 : 0.18, scale: selected ? 1 : 0.84 }}
        className="combined-persona-glyph-field"
        initial={false}
        transition={transition}
      />
      <m.span
        animate={animateSelection
          ? { opacity: [0.28, 0.82, 0.58], rotate: [-16, 5, -8], scale: [0.9, 1.08, 1] }
          : {
              opacity: selected ? 0.58 : 0.28,
              rotate: selected ? -8 : -16,
              scale: selected ? 1 : 0.9,
            }}
        className="combined-persona-glyph-orbit"
        initial={false}
        transition={transition}
      />
      <m.span
        animate={animateSelection
          ? { opacity: [0.2, 0.92, 0.56], scaleX: [0.08, 1, 0.72] }
          : { opacity: selected ? 0.56 : 0.2, scaleX: selected ? 0.72 : 0.08 }}
        className="combined-persona-glyph-trace"
        initial={false}
        transition={transition}
      />
      <m.span
        animate={animateSelection
          ? { rotate: [-4, 2, 0], scale: [0.94, 1.08, 1], y: [3, -4, 0] }
          : { rotate: selected ? 0 : -3, scale: selected ? 1 : 0.94, y: selected ? 0 : 1 }}
        className="combined-persona-glyph-core"
        initial={false}
        transition={transition}
      >
        <Icon />
      </m.span>
      <span className="combined-persona-glyph-node is-a" />
      <span className="combined-persona-glyph-node is-b" />
      <span className="combined-persona-glyph-node is-c" />
    </div>
  )
}

function PersonaLibraryScene({
  polished,
  reducedMotion,
  scene,
  selected,
}: {
  polished?: boolean
  reducedMotion: boolean
  scene:
    | (typeof specialistPersonaScenes)[number]
    | (typeof polishedSpecialistPersonaScenes)[number]
  selected: boolean
}) {
  const { PersonIcon, SignalIcon: RoleSignalIcon, ToolIcon, role } = scene
  const fieldShape = 'fieldShape' in scene ? scene.fieldShape : undefined
  const pose = 'pose' in scene ? scene.pose : undefined
  const fieldTransition = {
    duration: reducedMotion ? 0 : 0.25,
    ease: [0.215, 0.61, 0.355, 1] as [number, number, number, number],
  }

  return (
    <div
      aria-hidden="true"
      className={`combined-specialist-illustration is-${role}`}
      data-field-shape={fieldShape}
      data-icon-composition={polished ? 'reusable-library-persona-scene' : 'layered-persona-scene'}
      data-icon-source={polished ? 'tailwind-labs-heroicons-and-lucide' : 'tailwind-labs-heroicons'}
      data-illustration-state={selected ? 'selected' : 'preview'}
      data-role-illustration={role}
      data-scene-pose={pose}
    >
      {polished ? (
        <span className="combined-specialist-signal-field combined-specialist-signal-field--css" />
      ) : (
        <m.span
          animate={selected && !reducedMotion
            ? { opacity: [0, 0.92, 0.78], scale: [0.78, 1.08, 1] }
            : { opacity: selected ? 0.78 : 0, scale: selected ? 1 : 0.78 }}
          className="combined-specialist-signal-field"
          initial={false}
          transition={fieldTransition}
        />
      )}
      <span className="combined-specialist-scene" data-scene-role={role}>
        <span className="combined-specialist-scene-floor" />
        <span className="combined-specialist-scene-part is-person">
          <PersonIcon
            aria-hidden="true"
            className="combined-specialist-person-icon"
            focusable="false"
            strokeWidth={polished ? 1.05 : 1.18}
          />
        </span>
        <span className="combined-specialist-scene-part is-tool">
          <ToolIcon
            aria-hidden="true"
            className="combined-specialist-tool-icon"
            focusable="false"
            strokeWidth={polished ? 1.1 : 1.3}
          />
        </span>
        <span className="combined-specialist-scene-part is-signal">
          <RoleSignalIcon
            aria-hidden="true"
            className="combined-specialist-role-signal-icon"
            focusable="false"
            strokeWidth={polished ? 1.15 : 1.45}
          />
        </span>
        <span className="combined-specialist-scene-node is-a" />
        <span className="combined-specialist-scene-node is-b" />
      </span>
    </div>
  )
}

function CombinedPersonaSection({
  motionPolish = false,
  specialistLayout = false,
  specialistPolish = false,
}: {
  motionPolish?: boolean
  specialistLayout?: boolean
  specialistPolish?: boolean
}) {
  const reducedMotion = Boolean(useReducedMotion())

  return (
    <section
      aria-labelledby="combined-persona-title"
      className={`persona-section combined-persona-section${specialistLayout ? ' combined-persona-section--specialist-layout' : ''}`}
      data-icon-system={specialistLayout
        ? specialistPolish
          ? 'heroicons-lucide-reusable-persona-kit'
          : 'tailwind-heroicons-layered-personas'
        : motionPolish
          ? 'semantic-role-glyphs'
          : undefined}
      data-motion-mechanic={specialistLayout
        ? specialistPolish
          ? 'hover-figure-preview-selected-field-content-swap'
          : 'hover-preview-selection-content-swap'
        : motionPolish
          ? 'selected-role-field'
          : undefined}
      data-persona-layout={specialistLayout
        ? specialistPolish
          ? 'reusable-five-card-persona-scenes'
          : 'separate-five-card-reference-translation'
        : 'control'}
      data-sector="amplemarket-personas"
      id="why-wave99"
    >
      <div className="persona-heading combined-persona-heading">
        <h2
          aria-label={specialistPolish ? 'Built to power specialist revenue teams like you' : undefined}
          data-display-font={specialistPolish ? 'wave99-licensed-grotesk' : undefined}
          id="combined-persona-title"
        >
          {specialistLayout ? (
            specialistPolish ? (
              <>
                <span className="combined-specialist-heading-line">
                  <span className="combined-specialist-heading-built">
                    B<span className="combined-specialist-heading-alt">uil</span>t
                  </span>{' '}
                  to{' '}
                  <span className="combined-specialist-heading-doodle">
                    <SparklesIcon aria-hidden="true" className="is-left" />
                    <span>power</span>
                    <SparklesIcon aria-hidden="true" className="is-right" />
                  </span>{' '}
                  specialist
                </span>
                <span className="combined-specialist-heading-line">
                  revenue t<span className="combined-specialist-heading-alt">ea</span>ms like you
                </span>
              </>
            ) : (
              <>
                <span>Built to power specialist</span>{' '}
                <span>revenue teams like you</span>
              </>
            )
          ) : 'Built to power specialist revenue teams like you'}
        </h2>
      </div>
      <div className="combined-persona-stage">
        <TabGroup className={`persona-shell combined-persona-shell${specialistLayout ? ' combined-specialist-shell' : ''}`}>
          <TabList
            aria-label="Specialist revenue team roles"
            className="persona-tabs"
            data-hover-behavior={specialistLayout ? 'preview-art-without-selection' : undefined}
          >
            {personas.map((persona, index) => {
              const Icon = personaIcons[index]
              return (
                <Tab className="persona-tab" key={persona.label}>
                  {({ selected }) => (
                    <>
                      <span>{persona.label}</span>
                      {specialistLayout ? (
                        <PersonaLibraryScene
                          polished={specialistPolish}
                          reducedMotion={reducedMotion}
                          scene={specialistPolish
                            ? polishedSpecialistPersonaScenes[index]
                            : specialistPersonaScenes[index]}
                          selected={selected}
                        />
                      ) : motionPolish ? (
                        <PersonaRoleGlyph
                          glyph={personaRoleGlyphs[index]}
                          reducedMotion={reducedMotion}
                          selected={selected}
                        />
                      ) : (
                        <div aria-hidden="true" className="combined-persona-tab-visual">
                          <Icon />
                        </div>
                      )}
                      <i />
                    </>
                  )}
                </Tab>
              )
            })}
          </TabList>
          <TabPanels>
            {personas.map((persona) => (
              <TabPanel
                className={`persona-panel${specialistLayout ? ' combined-specialist-panel' : ''}`}
                key={persona.label}
              >
                <div className="persona-copy">
                  {!specialistLayout ? <p>{persona.label}</p> : null}
                  <h3>{persona.title}</h3>
                  {!specialistLayout ? <span>{persona.body}</span> : null}
                  <a href="#final-cta">Review the handoff route <ArrowRight /></a>
                </div>
                <div className="persona-outcomes">
                  {persona.outcomes.map(([title, body], index) => (
                    <article key={title}>
                      {!specialistLayout ? <small>0{index + 1}</small> : null}
                      <h4>{title}</h4>
                      <p>{body}</p>
                    </article>
                  ))}
                </div>
              </TabPanel>
            ))}
          </TabPanels>
        </TabGroup>
      </div>
    </section>
  )
}

type MetricResultCard = Extract<
  (typeof currentResultCards)[number],
  { readonly kind: 'metric' }
>

function CombinedMetricDoor({
  card,
  index,
  onBlurDoor,
  onFocusDoor,
  onHoverEndDoor,
  onHoverStartDoor,
  open,
  reducedMotion,
}: {
  card: MetricResultCard
  index: number
  onBlurDoor: () => void
  onFocusDoor: () => void
  onHoverEndDoor: () => void
  onHoverStartDoor: () => void
  open: boolean
  reducedMotion: boolean
}) {
  const accessibleLabel = open
    ? `${card.value} ${card.label}. Source record gated; publication approval required.`
    : `Inspect source status for ${card.value} ${card.label}`

  return (
    <m.button
      aria-label={accessibleLabel}
      aria-pressed={open}
      className={`combined-result-card combined-result-door is-metric tone-${card.tone}`}
      data-door-duration={reducedMotion ? '0ms' : '680ms'}
      data-door-mechanic="fixed-shell-stationary-back-front-hinge"
      data-door-state={open ? 'open' : 'closed'}
      onBlur={onBlurDoor}
      onClick={onFocusDoor}
      onFocus={onFocusDoor}
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return
        onBlurDoor()
        event.currentTarget.blur()
      }}
      onPointerEnter={(event) => {
        if (event.pointerType === 'mouse') onHoverStartDoor()
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === 'mouse') onHoverEndDoor()
      }}
      type="button"
    >
      <span aria-hidden="true" className="combined-result-door-back">
        <span className="combined-result-door-record">
          <LockKeyhole />
          <small>PRIVATE REVIEW · RECORD 0{index + 1}</small>
        </span>
        <strong>{card.record}</strong>
        <span>Source and publication approval must be attached before this result goes live.</span>
      </span>
      <m.span
        animate={reducedMotion
          ? { opacity: open ? 0 : 1, rotateY: 0, x: 0 }
          : { opacity: 1, rotateY: open ? -84 : 0, x: open ? -3 : 0 }}
        aria-hidden="true"
        className="combined-result-door-front"
        initial={false}
        transition={{
          duration: reducedMotion ? 0 : 0.68,
          ease: precisionEase,
        }}
      >
        <strong>{card.value}</strong>
        <span>{card.label}</span>
        <small className="combined-result-door-cue">
          <DoorOpen /> Inspect proof
        </small>
      </m.span>
    </m.button>
  )
}

function CombinedMetricFlip({
  card,
  onBlurCard,
  onFocusCard,
  onHoverEndCard,
  onHoverStartCard,
  open,
  reducedMotion,
}: {
  card: MetricResultCard
  onBlurCard: () => void
  onFocusCard: () => void
  onHoverEndCard: () => void
  onHoverStartCard: () => void
  open: boolean
  reducedMotion: boolean
}) {
  const accessibleLabel = open
    ? `${card.value} ${card.label}. Source review is open; publication approval is required.`
    : `Review source status for ${card.value} ${card.label}`

  return (
    <m.button
      aria-label={accessibleLabel}
      aria-pressed={open}
      className={`combined-result-card combined-result-flip is-metric tone-${card.tone}`}
      data-flip-duration={reducedMotion ? '0ms' : '750ms'}
      data-flip-mechanic="fixed-shell-full-card-y-rotation"
      data-flip-state={open ? 'open' : 'closed'}
      onBlur={onBlurCard}
      onClick={onFocusCard}
      onFocus={onFocusCard}
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return
        onBlurCard()
        event.currentTarget.blur()
      }}
      onPointerEnter={(event) => {
        if (event.pointerType === 'mouse') onHoverStartCard()
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === 'mouse') onHoverEndCard()
      }}
      type="button"
    >
      <m.span
        animate={{ rotateY: reducedMotion ? 0 : open ? 180 : 0 }}
        aria-hidden="true"
        className="combined-result-flip-inner"
        initial={false}
        transition={{
          duration: reducedMotion ? 0 : 0.75,
          ease: precisionEase,
        }}
      >
        <span className="combined-result-flip-face combined-result-flip-front">
          <strong>{card.value}</strong>
          <span>{card.label}</span>
          <small>WAVE99 · PRIVATE REVIEW</small>
        </span>
        <span className="combined-result-flip-face combined-result-flip-back">
          <span>Review source <ArrowRight /></span>
        </span>
      </m.span>
    </m.button>
  )
}

function CombinedResultsSection({
  fullCardFlip = false,
  motionPolish = false,
}: {
  fullCardFlip?: boolean
  motionPolish?: boolean
}) {
  const reducedMotion = Boolean(useReducedMotion())
  const [hoveredMetricIndex, setHoveredMetricIndex] = useState<number | null>(null)
  const [focusedMetricIndex, setFocusedMetricIndex] = useState<number | null>(null)
  const hoverExitTimer = useRef<number | null>(null)
  const activeMetricIndex = hoveredMetricIndex ?? focusedMetricIndex

  const openHoveredMetric = (index: number) => {
    if (hoverExitTimer.current !== null) window.clearTimeout(hoverExitTimer.current)
    hoverExitTimer.current = null
    setHoveredMetricIndex(index)
  }

  const closeHoveredMetric = (index: number) => {
    if (!fullCardFlip) {
      setHoveredMetricIndex((current) => (current === index ? null : current))
      return
    }

    if (hoverExitTimer.current !== null) window.clearTimeout(hoverExitTimer.current)
    hoverExitTimer.current = window.setTimeout(() => {
      setHoveredMetricIndex((current) => (current === index ? null : current))
      hoverExitTimer.current = null
    }, 64)
  }

  useEffect(() => () => {
    if (hoverExitTimer.current !== null) window.clearTimeout(hoverExitTimer.current)
  }, [])

  return (
    <section
      aria-labelledby="combined-results-title"
      className="combined-results-section"
      data-evidence-scope="provided-reference-copy"
      data-proof-status="requires-publication-approval"
      data-result-motion={fullCardFlip ? 'full-card-flip' : motionPolish ? 'front-hinge' : undefined}
      data-sector="current-results-mosaic"
      id="proof"
    >
      <div className="combined-results-heading">
        {!fullCardFlip && <p>CURRENT-SITE PROOF COMPOSITION · PRIVATE REVIEW</p>}
        <h2
          className="combined-proof-heading"
          data-type-rhythm="wave99-display-44-1.08--0.05"
          id="combined-results-title"
          {...(fullCardFlip ? { 'aria-label': 'Real results from real customers' } : {})}
        >
          {fullCardFlip ? (
            <span aria-hidden="true" className="combined-proof-heading-lines">
              <span>Real results from real</span>
              <span>customers</span>
            </span>
          ) : 'Real results from real customers'}
        </h2>
      </div>
      <div className="combined-results-mosaic">
        {currentResultCards.map((card, index) => {
          if (fullCardFlip && card.kind === 'metric') {
            return (
              <CombinedMetricFlip
                card={card}
                key={`${card.kind}-${index}`}
                onBlurCard={() => setFocusedMetricIndex((current) => (
                  current === index ? null : current
                ))}
                onFocusCard={() => setFocusedMetricIndex(index)}
                onHoverEndCard={() => closeHoveredMetric(index)}
                onHoverStartCard={() => openHoveredMetric(index)}
                open={activeMetricIndex === index}
                reducedMotion={reducedMotion}
              />
            )
          }

          if (motionPolish && card.kind === 'metric') {
            return (
              <CombinedMetricDoor
                card={card}
                index={index}
                key={`${card.kind}-${index}`}
                onBlurDoor={() => setFocusedMetricIndex((current) => (
                  current === index ? null : current
                ))}
                onFocusDoor={() => setFocusedMetricIndex(index)}
                onHoverEndDoor={() => closeHoveredMetric(index)}
                onHoverStartDoor={() => openHoveredMetric(index)}
                open={activeMetricIndex === index}
                reducedMotion={reducedMotion}
              />
            )
          }

          return (
            <article
              className={`combined-result-card is-${card.kind} tone-${card.tone}`}
              key={`${card.kind}-${index}`}
            >
              {card.kind === 'metric' ? (
                <>
                  <strong>{card.value}</strong>
                  <span>{card.label}</span>
                </>
              ) : (
                <>
                  <blockquote>&ldquo;{card.quote}&rdquo;</blockquote>
                  <p>{card.attribution}</p>
                </>
              )}
            </article>
          )
        })}
      </div>
      <p className="combined-results-approval-note">
        Customer figures and attributions remain private-review copy until publication approval is recorded.
      </p>
    </section>
  )
}

function CombinedCustomerLoveSection() {
  return (
    <section
      aria-labelledby="combined-customer-love-title"
      className="combined-customer-love"
      data-evidence-scope="provided-reference-copy"
      data-proof-status="requires-publication-approval"
      data-sector="localhost-4215-motion-current-copy"
      id="insights"
    >
      <div className="combined-customer-love-heading">
        <p>CUSTOMER LANGUAGE · MOTION YOU CAN PAUSE</p>
        <h2
          className="combined-proof-heading"
          data-type-rhythm="wave99-display-44-1.08--0.05"
          id="combined-customer-love-title"
        >
          Some love from our customers
        </h2>
      </div>
      <OperatingProofWall items={customerLoveItems} mode="testimonial" />
    </section>
  )
}

export default function CombinedApp({
  variant = 'control',
}: {
  variant?: CombinedVariant
} = {}) {
  const [reviewOpen, setReviewOpen] = useState(false)
  const [reviewSource, setReviewSource] = useState<ReviewSource>('hero')
  const [email, setEmail] = useState('')
  const usesCenteredIntro = variant !== 'control'
  const usesProofMotion = variant === 'proof-motion'
  const usesSpecialistPolish = variant === 'proof-flip-specialists-polished'
  const usesSpecialistTabs = variant === 'proof-flip-specialists' || usesSpecialistPolish
  const usesProofFlip = variant === 'proof-flip' || usesSpecialistTabs
  const usesRoleGlyphs = usesProofMotion || usesProofFlip

  const openReview = (source: ReviewSource) => {
    setReviewSource(source)
    setReviewOpen(true)
  }

  return (
    <LazyMotion features={domAnimation} strict>
      <div
        className={`site-shell combined-site-shell${usesCenteredIntro ? ' combined-site-shell--centered-intro' : ''}${usesProofMotion ? ' combined-site-shell--proof-motion' : ''}${usesProofFlip ? ' combined-site-shell--proof-flip' : ''}${usesSpecialistTabs ? ' combined-site-shell--specialist-tabs' : ''}${usesSpecialistPolish ? ' combined-site-shell--specialist-polished' : ''}`}
        data-candidate={usesSpecialistPolish
          ? 'combined-specialist-reusable-persona-polish'
          : usesSpecialistTabs
            ? 'combined-amplemarket-informed-specialist-tabs'
          : usesProofFlip
            ? 'combined-amplemarket-informed-full-card-proof-flip'
          : usesProofMotion
            ? 'combined-4215-proof-hinges-role-glyphs'
            : usesCenteredIntro
              ? 'combined-4215-centered-operating-intro'
              : 'combined-4215-product-zoom-wave99-proof-type'}
        data-layout-variant={variant}
        data-testimonial-cadence="shared"
      >
        <a className="skip-link" href="#main">Skip to content</a>
        <SiteHeader onReview={openReview} />
        <main id="main">
          <Hero
            demoScrollZoom="in-out"
            email={email}
            onReview={openReview}
            setEmail={setEmail}
          />
          <CurrentTrustSection />
          <ResultsFirstSection
            headingPlacement="stage"
            introAlignment={usesCenteredIntro ? 'centered' : 'control'}
            onReview={openReview}
            relayConcept="fit-common-circles-focus"
            surface="dark"
            stopAfterOutcome
          />
          <OrchidTestimonialSection />
          <PlatformSection proofCadence="control" testimonialStyle="combined-white" />
          <CombinedPersonaSection
            motionPolish={usesRoleGlyphs}
            specialistLayout={usesSpecialistTabs}
            specialistPolish={usesSpecialistPolish}
          />
          <CombinedResultsSection
            fullCardFlip={usesProofFlip}
            motionPolish={usesProofMotion}
          />
          <CombinedCustomerLoveSection />
          <FinalSection onReview={openReview} />
        </main>
        <ReviewDialog
          initialEmail={email}
          onClose={() => setReviewOpen(false)}
          open={reviewOpen}
          source={reviewSource}
        />
      </div>
    </LazyMotion>
  )
}
